import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  BookOpen,
  Clock3,
  Compass,
  Coffee,
  Download,
  ExternalLink,
  Gamepad2,
  Grid2X2,
  Heart,
  House,
  Images,
  Info,
  Link,
  MessageSquare,
  Music,
  ChevronLeft,
  ChevronRight,
  Presentation,
  PanelsTopLeft,
  PlayCircle,
  Send,
  ShieldCheck,
  Settings,
  Star
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { GalleryImage } from "./data/gallery";
import { statusLabels, type Project } from "./data/projects";
import {
  getPublicCategories,
  getPublicGalleryImages,
  getPublicProjects,
  getPublicResources,
  getPublicSiteContent
} from "./data/publicData";
import { resourceTypeLabels, type ResourceItem } from "./data/resources";
import { siteSettings } from "./data/siteSettings";
import type { NavIcon } from "./data/siteContent";

const siteContent = getPublicSiteContent();
const categories = getPublicCategories();
const visibleProjects = getPublicProjects();
const visibleResources = getPublicResources();
const visibleGalleryImages = getPublicGalleryImages();
const visibleFeedbackTargets = [...visibleProjects, ...visibleResources];

const sectionLinks = siteContent.navigation.filter((item) => item.showInToc);
const headerLinks = siteContent.navigation.filter((item) => item.showInHeader);

const categoryIcons = {
  "AI 與教材設計": BrainCircuit,
  "研習與現場工具": Presentation,
  "情境互動學習": Gamepad2,
  "工具與展示": PanelsTopLeft
};

const navIcons: Record<NavIcon, typeof House> = {
  home: House,
  star: Star,
  compass: Compass,
  grid: Grid2X2,
  clock: Clock3,
  book: BookOpen,
  image: Images,
  info: Info
};

const resourceIcons = {
  article: BookOpen,
  video: PlayCircle,
  github: PanelsTopLeft,
  tool: Link
};

function getMp4Fallback(videoUrl: string) {
  return videoUrl.replace(/\.(mov|quicktime)$/i, ".mp4");
}

type AnalyticsPoint = {
  date: string;
  visitors: number;
  pageViews: number;
};

type AdminAnalytics = {
  connected: boolean;
  source: string;
  summary: {
    totalVisitors: number;
    totalPageViews: number;
    averageVisitors: number;
  };
  dailyVisitors: AnalyticsPoint[];
  message: string;
};

function makeLocalAnalytics(): AdminAnalytics {
  const values = [18, 23, 21, 28, 34, 31, 42, 39, 45, 51, 48, 57, 63, 68];
  const today = new Date();
  const dailyVisitors = values.map((visitors, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (values.length - index - 1));
    return {
      date: date.toISOString().slice(0, 10),
      visitors,
      pageViews: Math.round(visitors * 1.8)
    };
  });
  const totalVisitors = dailyVisitors.reduce((total, item) => total + item.visitors, 0);
  const totalPageViews = dailyVisitors.reduce((total, item) => total + item.pageViews, 0);

  return {
    connected: false,
    source: "local_preview",
    summary: {
      totalVisitors,
      totalPageViews,
      averageVisitors: Math.round(totalVisitors / dailyVisitors.length)
    },
    dailyVisitors,
    message: "本機預覽資料；正式訪客統計需接 Cloudflare Web Analytics。"
  };
}

function sendPublicFeedback(payload: {
  targetType: "project" | "resource" | "site";
  targetId: string;
  rating?: number;
  liked?: boolean;
  comment?: string;
}) {
  return fetch("/api/public/feedback", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(payload)
  }).catch(() => null);
}

function useStoredEngagement(targetType: "project" | "resource", targetId: string) {
  const storageKey = `winnie-engagement-${targetType}-${targetId}`;
  const baseScore = 18 + (targetId.length % 7) * 3;
  const [rating, setRating] = useState(0);
  const [liked, setLiked] = useState(false);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as { rating?: number; liked?: boolean; note?: string };
      setRating(parsed.rating ?? 0);
      setLiked(Boolean(parsed.liked));
      setNote(parsed.note ?? "");
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const save = (next: { rating?: number; liked?: boolean; note?: string }) => {
    const payload = {
      rating,
      liked,
      note,
      ...next
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  };

  return {
    displayLikes: baseScore + (liked ? 1 : 0),
    liked,
    note,
    rating,
    savedNote,
    setLiked: () => {
      const nextLiked = !liked;
      setLiked(nextLiked);
      save({ liked: nextLiked });
      void sendPublicFeedback({ targetType, targetId, liked: nextLiked, rating });
    },
    setNote: (value: string) => {
      setNote(value);
      setSavedNote(false);
    },
    setRating: (value: number) => {
      setRating(value);
      save({ rating: value });
      void sendPublicFeedback({ targetType, targetId, rating: value, liked });
    },
    submitNote: () => {
      save({ note });
      void sendPublicFeedback({ targetType, targetId, rating, liked, comment: note });
      setSavedNote(true);
    }
  };
}

function useActiveSection() {
  const [activeSection, setActiveSection] = useState("top");

  useEffect(() => {
    const sections = sectionLinks
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => Boolean(section));

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        rootMargin: "-20% 0px -62% 0px",
        threshold: [0.08, 0.2, 0.45]
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return activeSection;
}

function HeaderMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const activeSection = useActiveSection();

  return (
    <div className={isOpen ? "header-menu is-open" : "header-menu"} id="site-menu" aria-hidden={!isOpen}>
      <div className="header-menu-title">快速前往</div>
      <div className="header-menu-links">
        {sectionLinks.map((item) => {
          const LinkIcon = navIcons[item.icon];

          return (
            <a
              key={item.id}
              className={activeSection === item.id ? "is-active" : ""}
              href={`#${item.id}`}
              onClick={onClose}
              aria-current={activeSection === item.id ? "true" : undefined}
              tabIndex={isOpen ? 0 : -1}
            >
              <span className="menu-icon" aria-hidden="true">
                <LinkIcon size={16} />
              </span>
              <span>{item.label}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function AppHero({ onExplore }: { onExplore: () => void }) {
  return (
    <section className="hero" aria-labelledby="site-title">
      <div className="hero-copy">
        <div className="eyebrow">{siteContent.hero.eyebrow}</div>
        <h1 id="site-title">{siteContent.hero.title}</h1>
        <p className="hero-subtitle">{siteContent.hero.subtitle}</p>
        <p className="hero-description">{siteContent.hero.description}</p>
        <div className="hero-actions">
          <button className="primary-action" type="button" onClick={onExplore}>
            <Grid2X2 size={18} aria-hidden="true" />
            瀏覽作品
          </button>
          <a className="secondary-action" href="#featured">
            <Star size={18} aria-hidden="true" />
            查看精選
          </a>
        </div>
      </div>

      <div className="brand-stage" aria-label="Winnie EdTech Lab 品牌動畫">
        <div className="video-orbit">
          <div className="video-frame">
            <video
              className="brand-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              disablePictureInPicture
            >
              <source src="/winnie-intro.mp4" type="video/mp4" />
            </video>
            <div className="video-fallback">W</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryStrip({
  activeCategory,
  onSelect
}: {
  activeCategory: string;
  onSelect: (category: string) => void;
}) {
  const filteredProjects = visibleProjects.filter(
    (project) => activeCategory === "全部" || project.category === activeCategory
  );

  return (
    <section className="section categories-section" id="categories" aria-labelledby="categories-title">
      <div className="section-heading">
        <p className="section-kicker">{siteContent.sections.categories.kicker}</p>
        <h2 id="categories-title">{siteContent.sections.categories.title}</h2>
      </div>
      <div className="category-grid">
        {categories.map((category) => {
          const count = visibleProjects.filter((project) => project.category === category.name).length;
          const CategoryIcon = categoryIcons[category.name as keyof typeof categoryIcons] ?? Grid2X2;

          return (
            <button
              key={category.name}
              className={activeCategory === category.name ? "category-tile is-active" : "category-tile"}
              type="button"
              onClick={() => onSelect(activeCategory === category.name ? "全部" : category.name)}
            >
              <span className="category-icon" aria-hidden="true">
                <CategoryIcon size={20} />
              </span>
              <span className="category-name">{category.name}</span>
              <small>{category.description}</small>
              <strong>{count} 件作品</strong>
            </button>
          );
        })}
      </div>

      <div className="category-results">
        <div className="filter-panel">
          <button
            className={activeCategory === "全部" ? "all-category is-active" : "all-category"}
            type="button"
            onClick={() => onSelect("全部")}
          >
            全部作品
          </button>
          <span>{filteredProjects.length} 件可瀏覽作品</span>
        </div>
        <div className="project-grid">
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, compact = false }: { project: Project; compact?: boolean }) {
  return (
    <article className={compact ? "project-card project-card-compact" : "project-card"}>
      <div className={`project-art tone-${project.imageTone}`} aria-hidden="true">
        <span className="project-art-label">{project.category}</span>
        <span className="project-art-title">{project.title.slice(0, 2)}</span>
      </div>
      <div className="project-body">
        <div className="project-meta">
          <span className={`status status-${project.status}`}>{statusLabels[project.status]}</span>
          {project.featured && <span className="pill">精選</span>}
        </div>
        <h3>{project.title}</h3>
        <p className="project-subtitle">{project.subtitle}</p>
        <p className="project-summary">{project.summary}</p>
        <div className="tag-row">
          {project.tags.slice(0, 4).map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <div className="feature-list" aria-label={`${project.title} 特色`}>
          {project.features.slice(0, 4).map((feature) => (
            <span key={feature}>{feature}</span>
          ))}
        </div>
      </div>
      <div className="project-actions">
        <a href={project.url} target="_blank" rel="noreferrer">
          開啟作品
          <ExternalLink size={16} aria-hidden="true" />
        </a>
        {project.guideUrl && (
          <a className="guide-link" href={project.guideUrl} target="_blank" rel="noreferrer">
            操作指南
          </a>
        )}
      </div>
      <EngagementPanel targetType="project" targetId={project.id} compact={compact} />
    </article>
  );
}

function EngagementPanel({
  targetType,
  targetId,
  compact = false
}: {
  targetType: "project" | "resource";
  targetId: string;
  compact?: boolean;
}) {
  const engagement = useStoredEngagement(targetType, targetId);

  if (!siteSettings.ratingEnabled) return null;

  return (
    <div className={compact ? "engagement-panel compact" : "engagement-panel"}>
      <div className="rating-row" aria-label="作品評分">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            className={engagement.rating >= value ? "rating-star is-active" : "rating-star"}
            type="button"
            onClick={() => engagement.setRating(value)}
            aria-label={`給 ${value} 星`}
            aria-pressed={engagement.rating === value}
          >
            <Star size={15} aria-hidden="true" />
          </button>
        ))}
        <span>{engagement.rating ? `${engagement.rating}/5` : "尚未評分"}</span>
      </div>
      <button
        className={engagement.liked ? "like-button is-liked" : "like-button"}
        type="button"
        onClick={engagement.setLiked}
        aria-pressed={engagement.liked}
      >
        <Heart size={15} aria-hidden="true" />
        {engagement.displayLikes} 人覺得實用
      </button>
      {!compact && (
        <form
          className="feedback-form"
          onSubmit={(event) => {
            event.preventDefault();
            engagement.submitNote();
          }}
        >
          <label>
            <span>給 Winnie 的建議</span>
            <textarea
              value={engagement.note}
              onChange={(event) => engagement.setNote(event.target.value)}
              maxLength={240}
              placeholder={siteSettings.feedbackEnabled ? "歡迎留下使用心得或想看的功能" : "目前暫不開放留言"}
              disabled={!siteSettings.feedbackEnabled}
            />
          </label>
          <div className="feedback-actions">
            <span>{siteSettings.feedbackEnabled ? `${engagement.note.length}/240` : "可先用星等與喜歡表達支持"}</span>
            <button type="submit" disabled={!siteSettings.feedbackEnabled || engagement.note.trim().length < 6}>
              <Send size={14} aria-hidden="true" />
              送出
            </button>
          </div>
          {engagement.savedNote && <p className="feedback-saved">感謝你的建議，Winnie 會仔細查看。</p>}
        </form>
      )}
    </div>
  );
}

function LatestSection() {
  const latestProjects = [...visibleProjects]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 3);

  return (
    <section className="section latest-section" id="latest" aria-labelledby="latest-title">
      <div className="section-heading spread">
        <div>
          <p className="section-kicker">{siteContent.sections.latest.kicker}</p>
          <h2 id="latest-title">{siteContent.sections.latest.title}</h2>
        </div>
        <p>最近整理或更新的作品，方便快速看見 Winnie EdTech Lab 近期重點。</p>
      </div>

      <div className="latest-cards">
        {latestProjects.map((project) => (
            <a
              key={project.id}
              className={`latest-card tone-${project.imageTone}`}
              href={project.url}
              target="_blank"
              rel="noreferrer"
            >
            <span>{project.updatedAt}</span>
            <strong>{project.title}</strong>
            <small>{project.summary}</small>
            <ArrowRight size={17} aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}

function ResourceCard({ resource }: { resource: ResourceItem }) {
  const ResourceIcon = resourceIcons[resource.type];

  return (
    <a className="resource-card" href={resource.url} target={resource.url.startsWith("#") ? undefined : "_blank"} rel="noreferrer">
      <span className="resource-icon" aria-hidden="true">
        <ResourceIcon size={20} />
      </span>
      <span className="resource-type">{resourceTypeLabels[resource.type]} · {resource.sourceName}</span>
      <strong>{resource.title}</strong>
      <p>{resource.summary}</p>
      <span className="resource-tags">
        {resource.tags.slice(0, 3).map((tag) => (
          <small key={tag}>{tag}</small>
        ))}
      </span>
    </a>
  );
}

function ResourcesSection() {
  return (
    <section className="section resources-section" id="resources" aria-labelledby="resources-title">
      <div className="section-heading spread">
        <div>
          <p className="section-kicker">{siteContent.sections.resources.kicker}</p>
          <h2 id="resources-title">{siteContent.sections.resources.title}</h2>
        </div>
        <p>{siteContent.sections.resources.description}</p>
      </div>
      <div className="resource-grid">
        {visibleResources.map((resource) => (
          <ResourceCard key={resource.id} resource={resource} />
        ))}
      </div>
    </section>
  );
}

function GalleryTile({ image }: { image: GalleryImage }) {
  return (
    <figure className={`gallery-tile tone-${image.tone}`}>
      {image.imageUrl ? (
        <img src={image.imageUrl} alt={image.alt} loading="lazy" />
      ) : (
        <div className="gallery-placeholder" role="img" aria-label={image.alt}>
          <span>{image.title}</span>
        </div>
      )}
      <figcaption>
        <strong>{image.title}</strong>
        <span>{image.subtitle}</span>
      </figcaption>
    </figure>
  );
}

function GallerySection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const activeImage = visibleGalleryImages[activeIndex] ?? visibleGalleryImages[0];
  const imageCount = visibleGalleryImages.length;

  useEffect(() => {
    if (imageCount <= 1) return;

    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % imageCount);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [imageCount]);

  const showPrevious = () => {
    setActiveIndex((index) => (index - 1 + imageCount) % imageCount);
  };

  const showNext = () => {
    setActiveIndex((index) => (index + 1) % imageCount);
  };

  if (!activeImage) return null;

  return (
    <section className="section gallery-section" id="gallery" aria-labelledby="gallery-title">
      <div className="section-heading spread">
        <div>
          <p className="section-kicker">{siteContent.sections.gallery.kicker}</p>
          <h2 id="gallery-title">{siteContent.sections.gallery.title}</h2>
        </div>
        <p>{siteContent.sections.gallery.description}</p>
      </div>
      <div
        className="gallery-shell"
        aria-label="AI 教學圖卡輪播"
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          if (touchStartX.current === null) return;
          const deltaX = event.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(deltaX) < 42) return;
          if (deltaX < 0) {
            showNext();
            return;
          }
          showPrevious();
        }}
      >
        <button className="gallery-nav previous" type="button" onClick={showPrevious} aria-label="上一張圖卡">
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <div className="gallery-stage" aria-live="polite">
          <GalleryTile image={activeImage} />
        </div>
        <button className="gallery-nav next" type="button" onClick={showNext} aria-label="下一張圖卡">
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </div>
      <div className="gallery-dots" aria-label="圖卡切換">
        {visibleGalleryImages.map((image, index) => (
          <button
            key={image.id}
            className={index === activeIndex ? "is-active" : ""}
            type="button"
            onClick={() => setActiveIndex(index)}
            aria-label={`切換到 ${image.title}`}
            aria-current={index === activeIndex ? "true" : undefined}
          />
        ))}
      </div>
    </section>
  );
}

function SupportSection() {
  if (!siteSettings.supportUrl) return null;

  return (
    <section className="support-strip" aria-label="支持 Winnie EdTech Lab">
      <div>
        <strong>喜歡這些作品嗎？</strong>
        <span>可以用一杯咖啡支持 Winnie 繼續整理教學工具與 AI 應用資源。</span>
      </div>
      <a href={siteSettings.supportUrl} target="_blank" rel="noreferrer">
        <Coffee size={17} aria-hidden="true" />
        Buy Me a Coffee
      </a>
    </section>
  );
}

function MaintenancePage() {
  const maintenanceMp4Url = getMp4Fallback(siteSettings.maintenanceVideoUrl);

  return (
    <main className="maintenance-page" aria-labelledby="maintenance-title">
      <section className="maintenance-card">
        <div className="video-orbit maintenance-orbit">
          <div className="video-frame">
            <video className="brand-video" autoPlay muted loop playsInline preload="auto" disablePictureInPicture>
              <source src={siteSettings.maintenanceVideoUrl} type="video/quicktime" />
              <source src={maintenanceMp4Url} type="video/mp4" />
            </video>
            <div className="video-fallback">W</div>
          </div>
        </div>
        <div className="maintenance-copy">
          <p className="section-kicker">網站整理中</p>
          <h1 id="maintenance-title">Winnie EdTech Lab 正在更新作品入口</h1>
          <p>我正在整理新的教學工具、AI 資源與作品展示。很快就會回來，謝謝你的耐心等待。</p>
        </div>
      </section>
    </main>
  );
}

function AdminPage() {
  const [settingsPreview, setSettingsPreview] = useState(siteSettings);
  const [apiStatus, setApiStatus] = useState("檢查中");
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("winnieAdminToken") ?? "");
  const [adminMessage, setAdminMessage] = useState("尚未連線管理 API");
  const [analytics, setAnalytics] = useState<AdminAnalytics>(() => makeLocalAnalytics());
  const totalLikes = visibleFeedbackTargets.reduce((total, item) => total + 18 + (item.id.length % 7) * 4, 0);
  const averageRating = 4.6;
  const maxVisitors = Math.max(...analytics.dailyVisitors.map((item) => item.visitors), 1);

  useEffect(() => {
    let cancelled = false;

    async function checkApi() {
      try {
        const response = await fetch("/api/public/home", { headers: { accept: "application/json" } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (!cancelled) setApiStatus("公開 API 可讀取");
      } catch {
        if (!cancelled) setApiStatus("本機 Vite 預覽中，Cloudflare Functions 部署後啟用");
      }
    }

    void checkApi();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadAnalytics() {
      try {
        const response = await fetch("/api/admin/analytics", {
          headers: {
            accept: "application/json",
            ...(adminToken ? { "X-Admin-Token": adminToken } : {})
          }
        });
        const payload = (await response.json()) as AdminAnalytics & { ok?: boolean };

        if (!cancelled && response.ok && payload.dailyVisitors) {
          setAnalytics(payload);
        }
      } catch {
        if (!cancelled) setAnalytics(makeLocalAnalytics());
      }
    }

    void loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [adminToken]);

  const saveAdminToken = (value: string) => {
    setAdminToken(value);
    if (value.trim()) {
      localStorage.setItem("winnieAdminToken", value.trim());
      return;
    }
    localStorage.removeItem("winnieAdminToken");
  };

  const patchAdminSettings = async (nextSettings: Partial<typeof siteSettings>) => {
    setSettingsPreview((current) => ({ ...current, ...nextSettings }));

    try {
      const response = await fetch("/api/admin/site-settings", {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          ...(adminToken ? { "X-Admin-Token": adminToken } : {})
        },
        body: JSON.stringify(nextSettings)
      });
      const payload = (await response.json()) as { ok?: boolean; message?: string; error?: string };

      if (!response.ok || !payload.ok) {
        setAdminMessage(payload.message ?? `管理 API 尚未完成寫入：${payload.error ?? response.status}`);
        return;
      }

      setAdminMessage("管理 API 已寫入設定");
    } catch {
      setAdminMessage("本機 Vite 不會執行 Cloudflare Functions；部署或 Wrangler Pages 預覽後才可寫入。");
    }
  };

  const downloadAdminCsv = async (dataset: "catalog" | "feedback" | "settings") => {
    try {
      const response = await fetch(`/api/admin/export?dataset=${dataset}`, {
        headers: {
          ...(adminToken ? { "X-Admin-Token": adminToken } : {})
        }
      });

      if (!response.ok) {
        setAdminMessage(`匯出失敗：${response.status}`);
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("content-disposition") ?? "";
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? `winnie-${dataset}.csv`;
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setAdminMessage(`已匯出 ${filename}`);
    } catch {
      setAdminMessage("本機 Vite 不會執行 Cloudflare Functions；部署或 Wrangler Pages 預覽後才可匯出。");
    }
  };

  return (
    <main className="admin-page" aria-labelledby="admin-title">
      <section className="admin-hero">
        <div>
          <p className="section-kicker">Winnie 管理中心</p>
          <h1 id="admin-title">網站狀態與內容管理</h1>
          <p>這裡是第一版管理頁雛形。正式部署後會接 Cloudflare Access、Worker API、資料儲存與留言寄信。</p>
        </div>
        <div className="admin-status-card">
          <Settings size={22} aria-hidden="true" />
          <strong>{apiStatus}</strong>
          <span>目前可先檢視結構與操作流程。</span>
        </div>
      </section>

      <section className="admin-token-panel" aria-label="管理 API Token">
        <label>
          <span>管理 Token</span>
          <input
            type="password"
            value={adminToken}
            onChange={(event) => saveAdminToken(event.target.value)}
            placeholder="部署後填入 Cloudflare Secret 對應的測試 Token"
          />
        </label>
        <p>{adminMessage}</p>
      </section>

      <section className="admin-grid" aria-label="網站設定">
        <article className="admin-card">
          <div className="admin-card-heading">
            <span>
              <ShieldCheck size={18} aria-hidden="true" />
              整站狀態
            </span>
            <button
              type="button"
              onClick={() => void patchAdminSettings({ maintenanceMode: !settingsPreview.maintenanceMode })}
            >
              {settingsPreview.maintenanceMode ? "切回公開" : "切到施工中"}
            </button>
          </div>
          <strong>{settingsPreview.maintenanceMode ? "施工中模式預覽" : "公開瀏覽中"}</strong>
          <p>正式版會透過 `/api/admin/site-settings` 寫入 Cloudflare KV，並由 middleware 顯示施工中頁面。</p>
        </article>

        <article className="admin-card">
          <div className="admin-card-heading">
            <span>
              <MessageSquare size={18} aria-hidden="true" />
              留言建議
            </span>
            <button
              type="button"
              onClick={() => void patchAdminSettings({ feedbackEnabled: !settingsPreview.feedbackEnabled })}
            >
              {settingsPreview.feedbackEnabled ? "暫停留言" : "預覽開放"}
            </button>
          </div>
          <strong>{settingsPreview.feedbackEnabled ? "留言入口預覽開放" : "留言入口暫停"}</strong>
          <p>留言正式開放前會先接 Turnstile、頻率限制、內容檢查與後端寄信，不會暴露你的真實信箱。</p>
        </article>

        <article className="admin-card">
          <div className="admin-card-heading">
            <span>
              <BarChart3 size={18} aria-hidden="true" />
              喜好統計
            </span>
            <span className="admin-chip">雛形</span>
          </div>
          <strong>{totalLikes} 次喜歡 · 平均 {averageRating}/5</strong>
          <p>正式版會依作品、資源、載具與日期統計星等、喜歡次數與異常操作。</p>
        </article>

        <article className="admin-card">
          <div className="admin-card-heading">
            <span>
              <Coffee size={18} aria-hidden="true" />
              Buy Me a Coffee
            </span>
            <span className="admin-chip">{settingsPreview.supportUrl ? "已設定" : "未設定"}</span>
          </div>
          <strong>{settingsPreview.supportUrl || "等待 Winnie 提供連結"}</strong>
          <p>填入 `supportUrl` 後，公開網站才會顯示支持區塊；空白時不顯示。</p>
        </article>
      </section>

      <section className="admin-domain-panel" aria-labelledby="admin-domain-title">
        <div>
          <p className="section-kicker">正式網域</p>
          <h2 id="admin-domain-title">winnie.tw 切換狀態</h2>
          <p>
            新版已部署到 <strong>winnie-home.pages.dev</strong>，`winnie.tw` 與 `www.winnie.tw` 已加入 Pages custom
            domains，但目前 Cloudflare 回報 `CNAME record not set`。
          </p>
        </div>
        <ol>
          <li>到 Cloudflare DNS。</li>
          <li>將 `@` 與 `www` 指向 `winnie-home.pages.dev`。</li>
          <li>回到 Pages 專案 `winnie-home` 重新驗證 custom domains。</li>
        </ol>
      </section>

      <section className="admin-analytics-section" aria-labelledby="admin-analytics-title">
        <div className="section-heading spread">
          <div>
            <p className="section-kicker">流量與成效</p>
            <h2 id="admin-analytics-title">每日訪客趨勢</h2>
          </div>
          <p>{analytics.message}</p>
        </div>
        <div className="admin-metric-grid">
          <article>
            <span>近 14 日訪客</span>
            <strong>{analytics.summary.totalVisitors}</strong>
          </article>
          <article>
            <span>近 14 日瀏覽</span>
            <strong>{analytics.summary.totalPageViews}</strong>
          </article>
          <article>
            <span>日均訪客</span>
            <strong>{analytics.summary.averageVisitors}</strong>
          </article>
          <article>
            <span>資料來源</span>
            <strong>{analytics.connected ? "已串接" : "待串接"}</strong>
          </article>
        </div>
        <div className="admin-chart" role="img" aria-label="近 14 日每日訪客長條圖">
          {analytics.dailyVisitors.map((item) => (
            <div className="admin-chart-bar" key={item.date}>
              <span>{item.visitors}</span>
              <i style={{ height: `${Math.max(12, (item.visitors / maxVisitors) * 100)}%` }} />
              <small>{item.date.slice(5)}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-export-panel" aria-labelledby="admin-export-title">
        <div>
          <p className="section-kicker">資料匯出</p>
          <h2 id="admin-export-title">Excel 可開啟紀錄檔</h2>
          <p>匯出為 UTF-8 CSV，Excel 可直接開啟；正式回饋資料需完成 KV 綁定後才會有完整紀錄。</p>
        </div>
        <div>
          <button type="button" onClick={() => downloadAdminCsv("catalog")}>
            <Download size={16} aria-hidden="true" />
            內容清單 CSV
          </button>
          <button type="button" onClick={() => downloadAdminCsv("feedback")}>
            <Download size={16} aria-hidden="true" />
            評分留言 CSV
          </button>
          <button type="button" onClick={() => downloadAdminCsv("settings")}>
            <Download size={16} aria-hidden="true" />
            網站設定 CSV
          </button>
        </div>
      </section>

      <section className="admin-table-section" aria-labelledby="admin-content-title">
        <div className="section-heading spread">
          <div>
            <p className="section-kicker">內容盤點</p>
            <h2 id="admin-content-title">目前可管理項目</h2>
          </div>
          <p>後續會改由管理 API 讀取完整資料，包含公開、隱藏、私有、待審與缺資料項目。</p>
        </div>
        <div className="admin-table">
          <div className="admin-table-row header">
            <span>類型</span>
            <span>名稱</span>
            <span>狀態</span>
            <span>互動</span>
          </div>
          {visibleFeedbackTargets.map((item) => (
            <div className="admin-table-row" key={item.id}>
              <span>{"sourceName" in item ? "資源" : "作品"}</span>
              <strong>{item.title}</strong>
              <span>公開</span>
              <span>{18 + (item.id.length % 7) * 4} 喜歡</span>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-note">
        <strong>下一步要接上的正式能力</strong>
        <p>
          Cloudflare Access 保護 `/admin`、KV 或 D1 儲存設定與回饋、Turnstile 防濫用、後端寄信、Google Sheet 同步與
          Google Drive 圖片資料夾同步。
        </p>
      </section>
    </main>
  );
}

function AppFooter() {
  return (
    <footer>
      <div className="copyright-panel" aria-label="著作權宣言">
        <strong>著作權宣言</strong>
        <p>
          本站由 Winnie EdTech Lab 以教學專業為核心，結合 ChatGPT、Gemini、Claude、Grok
          等 AI 平臺協作規劃、撰寫、設計與開發。網站內容、教學設計、圖文整理與作品呈現，除另有標示外，著作權由 Winnie
          EdTech Lab 保留；引用、分享或改作請註明來源，並尊重原始素材與第三方服務之權利。
        </p>
      </div>
      <div className="footer-container">
        <div className="winnie-badge">🐻 Winnie EdTech Lab</div>
        <div className="footer-text">© 2026 Developed by Winnie Lab | 專業讓你稱職，熱情使你傑出</div>
      </div>
    </footer>
  );
}

export function App() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [glowStyle, setGlowStyle] = useState({ left: "50%", top: "50%", opacity: 0 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [musicBlocked, setMusicBlocked] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isAdminRoute = window.location.pathname.startsWith("/admin");

  useEffect(() => {
    const saved = sessionStorage.getItem("winnieMusicEnabled");
    const wantsMusic = saved !== "false";
    if (!wantsMusic || !audioRef.current) return;

    audioRef.current.volume = 0.32;
    audioRef.current
      .play()
      .then(() => {
        setMusicEnabled(true);
        setMusicBlocked(false);
      })
      .catch(() => {
        setMusicEnabled(false);
        setMusicBlocked(true);
      });
  }, []);

  const toggleMusic = async () => {
    if (!audioRef.current) return;
    audioRef.current.volume = 0.32;

    if (musicEnabled) {
      audioRef.current.pause();
      sessionStorage.setItem("winnieMusicEnabled", "false");
      setMusicEnabled(false);
      setMusicBlocked(false);
      return;
    }

    try {
      await audioRef.current.play();
      sessionStorage.setItem("winnieMusicEnabled", "true");
      setMusicEnabled(true);
      setMusicBlocked(false);
    } catch {
      setMusicEnabled(false);
      setMusicBlocked(true);
    }
  };

  const jumpToProjects = () => {
    document.getElementById("categories")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (siteSettings.maintenanceMode) {
    return <MaintenancePage />;
  }

  if (isAdminRoute) {
    return <AdminPage />;
  }

  return (
    <>
      <div
        className="cursor-glow"
        style={glowStyle}
        aria-hidden="true"
        onTransitionEnd={() => undefined}
      />
      <div
        className="app-shell"
        onPointerMove={(event) => {
          if (!window.matchMedia("(hover:hover) and (pointer:fine)").matches) return;
          setGlowStyle({ left: `${event.clientX}px`, top: `${event.clientY}px`, opacity: 1 });
        }}
        onPointerLeave={() => setGlowStyle((previous) => ({ ...previous, opacity: 0 }))}
      >
        <header className="topbar">
          <button
            className={isMenuOpen ? "brand-mark is-active" : "brand-mark"}
            type="button"
            onClick={() => setIsMenuOpen((value) => !value)}
            aria-expanded={isMenuOpen}
            aria-controls="site-menu"
            aria-label={isMenuOpen ? "收合快速目錄" : "展開快速目錄"}
            title={isMenuOpen ? "收合目錄" : "展開目錄"}
          >
            W
          </button>
          <nav aria-label="主要導覽">
            {headerLinks.map((item) => {
              const LinkIcon = navIcons[item.icon];

              return (
                <a key={item.id} href={`#${item.id}`} onClick={() => setIsMenuOpen(false)}>
                  <LinkIcon size={15} aria-hidden="true" />
                  {item.label}
                </a>
              );
            })}
          </nav>
          <button
            className={musicEnabled ? "music-control is-on" : "music-control"}
            type="button"
            onClick={toggleMusic}
            aria-pressed={musicEnabled}
            aria-label={musicEnabled ? "關閉背景音樂" : "開啟背景音樂"}
            title={musicEnabled ? "關閉背景音樂" : musicBlocked ? "開啟背景音樂" : "背景音樂"}
          >
            <Music size={16} aria-hidden="true" />
          </button>
          <HeaderMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
        </header>

        <audio ref={audioRef} loop preload="auto">
          <source src="/winnie-bgm.mp3" type="audio/mpeg" />
        </audio>

        <main id="top">
          <AppHero onExplore={jumpToProjects} />

          <section className="section featured-section" id="featured" aria-labelledby="featured-title">
            <div className="section-heading spread">
              <div>
                <p className="section-kicker">{siteContent.sections.featured.kicker}</p>
                <h2 id="featured-title">{siteContent.sections.featured.title}</h2>
              </div>
              <p>{siteContent.sections.featured.description}</p>
            </div>
            <div className="featured-grid">
              {visibleProjects
                .filter((project) => project.featured)
                .slice(0, 3)
                .map((project) => (
                  <ProjectCard key={project.id} project={project} compact />
                ))}
            </div>
          </section>

          <LatestSection />
          <CategoryStrip activeCategory={activeCategory} onSelect={setActiveCategory} />
          <ResourcesSection />
          <GallerySection />
          <SupportSection />

          <section className="section about-section" id="about" aria-labelledby="about-title">
            <div>
              <p className="section-kicker">{siteContent.sections.about.kicker}</p>
              <h2 id="about-title">{siteContent.sections.about.title}</h2>
            </div>
            <p>{siteContent.sections.about.description}</p>
            <div className="trust-notes">
              <span>
                <ShieldCheck size={16} aria-hidden="true" />
                留言與統計功能正式開放前，會先加入驗證、頻率限制與審核機制。
              </span>
            </div>
            <a className="secondary-action inline-action" href="#categories">
              <Grid2X2 size={18} aria-hidden="true" />
              瀏覽作品分類
            </a>
          </section>
        </main>

        <AppFooter />
      </div>
    </>
  );
}
